'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, asText, isFilled } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { useIsClient } from '@/hooks';
import { AccordionCard, SectionHeader } from './components';
import type { AccordionItemView } from './components';

export type ServiceListAccordionProps =
  SliceComponentProps<Content.ServiceListAccordionSlice>;

const emptyDetails = {
  detailsIntro: '',
  leftColumnTitle: '',
  rightColumnTitle: '',
  detailRows: [] as AccordionItemView['detailRows'],
};

const ServiceListAccordion: FC<ServiceListAccordionProps> = ({ slice }) => {
  const isIncludesList = slice.variation === 'includesList';
  const { title, subtitle } = slice.primary;
  const headerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const isClient = useIsClient();

  const titleText = isFilled.richText(title) ? asText(title) : '';
  const subtitleText = isFilled.richText(subtitle) ? asText(subtitle) : '';
  const descriptionText =
    slice.variation === 'default' &&
    isFilled.richText(slice.primary.description)
      ? asText(slice.primary.description)
      : '';

  const cards: AccordionItemView[] = isIncludesList
    ? slice.primary.items.map((item) => ({
        itemTitle: item.item_title ?? '',
        summary: isFilled.richText(item.item_summary)
          ? asText(item.item_summary)
          : '',
        tags:
          item.tags
            ?.map((tag) => tag.label)
            .filter((label): label is string => Boolean(label)) ?? [],
        ...emptyDetails,
        includesHeading: item.includes_heading ?? '',
        includeItems:
          item.include_items
            ?.map((row) => row.text)
            .filter((text): text is string => Boolean(text)) ?? [],
      }))
    : slice.primary.items.map((item) => ({
        itemTitle: item.item_title ?? '',
        summary: isFilled.richText(item.item_summary)
          ? asText(item.item_summary)
          : '',
        tags:
          item.tags
            ?.map((tag) => tag.label)
            .filter((label): label is string => Boolean(label)) ?? [],
        detailsIntro: item.details_intro ?? '',
        leftColumnTitle: item.left_column_title ?? '',
        rightColumnTitle: item.right_column_title ?? '',
        detailRows:
          item.detail_rows
            ?.map((row) => ({
              label: row.label ?? '',
              text: row.text ?? '',
            }))
            .filter((row) => row.label || row.text) ?? [],
        includesHeading: '',
        includeItems: [],
      }));

  useEffect(() => {
    if (!isClient) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -10% 0px',
      },
    );

    const headerEl = headerRef.current;
    const cardsEl = cardsRef.current;

    if (headerEl) {
      headerEl.style.transitionDelay = '0ms';
      observer.observe(headerEl);
    }

    if (cardsEl) {
      cardsEl.style.transitionDelay = '200ms';
      observer.observe(cardsEl);
    }

    return () => {
      if (headerEl) {
        observer.unobserve(headerEl);
      }
      if (cardsEl) {
        observer.unobserve(cardsEl);
      }
    };
  }, [isClient]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#262626] px-5 py-10 md:px-8 md:py-[70px] lg:px-20"
    >
      <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-6 md:gap-9">
        {(titleText || subtitleText || descriptionText) && (
          <div
            ref={headerRef}
            className="w-full opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            <SectionHeader
              title={titleText}
              subtitle={subtitleText}
              description={descriptionText}
            />
          </div>
        )}

        {cards.length > 0 && (
          <div
            ref={cardsRef}
            className="flex w-full flex-col gap-6 opacity-0 translate-y-8 transition-all duration-700 ease-out md:gap-9"
          >
            {cards.map((item, index) => (
              <AccordionCard
                key={`${item.itemTitle}-${index}`}
                item={item}
                layout={isIncludesList ? 'includes' : 'details'}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ServiceListAccordion;
